/**
 * Qué parte de la imagen se lee, y qué lecturas cuentan.
 *
 * The pure half of the camera scanner, kept apart from `escaner.ts` so it can be tested without
 * a camera, a canvas or a WebAssembly module. Everything here is a decision somebody would
 * otherwise have to make by watching a phone: which pixels to hand the reader, which codes to
 * believe, and when the same code in front of the lens is one can and when it is two.
 */

import { CODIGO_MAX, esGtin, limpiarCodigo } from '@abastece/shared';

export interface Rect {
  x: number;
  y: number;
  ancho: number;
  alto: number;
}

/**
 * The part of the video frame under the viewfinder, in the frame's own pixels.
 *
 * The video is drawn `object-fit: cover`: scaled until it fills its box and the overflow cut
 * off equally on both sides, so what the viewfinder frames on screen is not the middle of the
 * camera's picture in any simple proportion. Reading only that part is what makes the box on
 * screen mean something — with three products on the counter, the one inside it is the one
 * that rings up — and a smaller picture is also a faster read on a cheap phone.
 *
 * `margen` widens the box on every side by that share of its size, so a barcode that spills a
 * little past the drawn corners still reads. The result is clipped to the frame.
 */
export function recorteDelVisor(
  video: { ancho: number; alto: number },
  caja: { ancho: number; alto: number },
  visor: Rect,
  margen = 0.15,
): Rect {
  const escala = Math.max(caja.ancho / video.ancho, caja.alto / video.alto);
  const dx = (caja.ancho - video.ancho * escala) / 2;
  const dy = (caja.alto - video.alto * escala) / 2;

  const mx = visor.ancho * margen;
  const my = visor.alto * margen;
  const x0 = Math.max(0, (visor.x - mx - dx) / escala);
  const y0 = Math.max(0, (visor.y - my - dy) / escala);
  const x1 = Math.min(video.ancho, (visor.x + visor.ancho + mx - dx) / escala);
  const y1 = Math.min(video.alto, (visor.y + visor.alto + my - dy) / escala);

  return {
    x: Math.round(x0),
    y: Math.round(y0),
    ancho: Math.max(0, Math.round(x1 - x0)),
    alto: Math.max(0, Math.round(y1 - y0)),
  };
}

export interface Lectura {
  codigo: string;
  /** As the reader names it: `ean_13`, `upc_a`, `code_128`… */
  formato: string;
}

/**
 * A read worth believing, cleaned, or `null`.
 *
 * Both readers already check the digit at the end of an EAN or a UPC; this checks again,
 * because the phone's own reader is whatever that phone ships and a misread rung up is a
 * wrong product on a customer's ticket. ITF is taken only as ITF-14, the 14 digits on the
 * carton a case of soda comes in: at other lengths it is the format most prone to finding a
 * barcode in the stripes of a label.
 */
export function aceptarLectura(valor: string, formato: string): Lectura | null {
  const codigo = limpiarCodigo(valor);
  if (!codigo || codigo.length > CODIGO_MAX) return null;
  switch (formato) {
    case 'ean_13':
    case 'ean_8':
    case 'upc_a':
      return esGtin(codigo) ? { codigo, formato } : null;
    case 'upc_e':
      return /^\d{6,8}$/.test(codigo) ? { codigo, formato } : null;
    case 'itf':
      return codigo.length === 14 && esGtin(codigo) ? { codigo, formato } : null;
    default:
      return { codigo, formato };
  }
}

export interface FiltroLecturas {
  /** Every read goes through here, with when it happened; `true` means it counts. */
  leer(codigo: string, ahora: number): boolean;
  /** This code does not count again until it has been out of sight for a while. */
  silenciar(codigo: string, ahora: number): void;
}

/**
 * Which reads count.
 *
 *   · **A code counts on its second read within `confirmarEn`.** A real barcode is read about
 *     ten times a second, so waiting for the second costs a tenth of a second; one frame's
 *     misread never rings anything up.
 *   · **Having counted, it does not count again while it stays in front of the lens.** Every
 *     read of it pushes the moment back, and it re-arms only once it has gone unread for
 *     `rearmarTras`. Holding a can still is one can; taking it away and bringing it — or the
 *     next one of the same — back is two.
 */
export function crearFiltroLecturas({ confirmarEn = 1_000, rearmarTras = 1_200 } = {}): FiltroLecturas {
  const primeraVez = new Map<string, number>();
  const contado = new Map<string, number>();

  return {
    leer(codigo, ahora) {
      const visto = contado.get(codigo);
      if (visto !== undefined && ahora - visto < rearmarTras) {
        contado.set(codigo, ahora);
        return false;
      }
      contado.delete(codigo);

      const antes = primeraVez.get(codigo);
      if (antes !== undefined && ahora - antes <= confirmarEn) {
        primeraVez.delete(codigo);
        contado.set(codigo, ahora);
        return true;
      }
      primeraVez.set(codigo, ahora);
      return false;
    },

    silenciar(codigo, ahora) {
      primeraVez.delete(codigo);
      contado.set(codigo, ahora);
    },
  };
}
